// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { inject, ErrorHandler, isDevMode, provideAppInitializer, importProvidersFrom } from '@angular/core';
import { provideRouter, RouteReuseStrategy, withDisabledInitialNavigation } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { TranslocoService, getBrowserLang, provideTransloco } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { httpHandlerInterceptor } from '@core/interceptors/http-handler.interceptor';
import { Auth2Service } from '@core/auth/auth.service';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fad } from '@fortawesome/pro-duotone-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { TranslocoHttpLoader } from 'src/transloco-loader';
import { provideToastr } from 'ngx-toastr';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { GlobalErrorHandler } from '@core/error/global-error-handler';
import { LoggerService } from '@core/services/logger.service';
import { OfflineStore } from '@core/offline/offline-store.service';
import { offlineCacheInterceptor } from '@core/offline/offline-cache.interceptor';
import { provideGuidedTours } from '@ergoss/guided-tour';
import { ToursService } from '@core/tour/tours.service';
import { TourSeenApiService } from '@core/tour/tour-seen-api.service';
import { DeepLinkService } from '@core/services/deep-link.service';

function initializeApp() {
  const transloco = inject(TranslocoService);
  const authService = inject(Auth2Service);
  const iconLib = inject(FaIconLibrary);
  const logger = inject(LoggerService);

  // Backstop for errors Angular's zone-based ErrorHandler can't see — out-of-zone
  // async, unhandled promise rejections, raw window errors (e.g. the OAuth XHR).
  // Routed to the same LoggerService seam so observability captures everything.
  window.addEventListener('unhandledrejection', (e) => logger.error('unhandledRejection', e.reason));
  window.addEventListener('error', (e) => logger.error('windowError', e.error ?? e.message));
  // set transloco language
  let language: string | null | undefined = localStorage.getItem('language') ?? 'en';
  if (language) transloco.setActiveLang(language);
  else {
    language = getBrowserLang();
    if (language) {
      if (language.includes('zh')) language = 'zh-CN';

      transloco.setActiveLang(language);
      localStorage.setItem('language', language);
    }
  }
  // Offline store (plan B1): warm the encrypted store shortly AFTER boot —
  // fire and forget, never a boot dependency (it resolves false on web by
  // design and on any failure). Deferred past the first paint as boot
  // hygiene: an offline-store warm-up has no business in the pre-paint
  // window, and the store has no consumers before the read path lands. Dev
  // builds log a boot counter so a bed pass can prove persistence across
  // relaunches straight from the console.
  const offlineStore = inject(OfflineStore);
  setTimeout(() => {
    void offlineStore.ensureReady().then((ready) => {
      if (ready && isDevMode()) return offlineStore.logBootDiagnostics();
      return undefined;
    });
  }, 3000);

  // iOS Universal Links: attach the deep-link listener BEFORE login so a
  // cold-launch link (e.g. an email CTA) is parked in redirectAfterLogin before
  // the claims effect consumes it. Fire-and-forget; no-ops off iOS.
  void inject(DeepLinkService).init();
  // init authentacation service
  authService.init();
  // launch login proceddure
  authService.login();
  // add icons to library — fad (duotone) is the default used everywhere via
  // sara-icon; fas (solid) + far (regular) are registered so direct <fa-icon>
  // call sites can render solid/outline pairs (e.g. the bookmark toggle on
  // the flights toolbar — solid when active, regular outline when inactive).
  iconLib.addIconPacks(fad, fas, far);
  return firstValueFrom(transloco.load(language as string));
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideIonicAngular({ mode: 'ios' }),
    // Initial navigation is disabled so the router doesn't briefly resolve
    // `<origin>?code=...` (the post-OAuth redirect URL) to MainComponent at
    // `path: ''` before app.component's claims effect navigates to the saved
    // deep-link target (`redirectAfterLogin`, e.g. `/replay/<mlid>`). The
    // claims effect calls `router.navigateByUrl(...)` once claims arrive,
    // which acts as the initial navigation.
    provideRouter(routes, withDisabledInitialNavigation()),
    // offlineCache BEFORE httpHandler: it keys on the clean relative URL and an
    // offline cache hit must answer before the handler's device-offline
    // fail-fast (plan B2; OfflineMode-flag-gated inside the interceptor).
    provideHttpClient(withInterceptors([offlineCacheInterceptor, httpHandlerInterceptor])),
    provideToastr(),
    provideTransloco({
      config: {
        fallbackLang: 'en',
        missingHandler: {
          // It will use the first language set in the `fallbackLang` property
          useFallbackTranslation: true,
        },
        availableLangs: ['en', 'fr', 'zh-CN', 'es-mx'],
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => initializeApp()),
    // Guided-tour engine seams (@ergoss/guided-tour, RFC-003): the app
    // supplies the tour content (PwiApi-backed, local fallback while the
    // store is unseeded), the per-crew seen-state (PwiApi — roams devices),
    // and its logger.
    provideGuidedTours({ source: ToursService, logger: LoggerService, stateSource: TourSeenApiService }),
    importProvidersFrom(
      MonacoEditorModule.forRoot({
        baseUrl: './assets/monaco/min/vs',
      }),

      NgxSkeletonLoaderModule.forRoot(),
    ),
  ],
};
